import { CustomerPermissionOnly, CustomerRoleOnly } from '@/api/apis/customer';
import {
  CUSTOMER_ROLE_NAMES,
  CUSTOMER_PERMISSION_NAMES,
  CUSTOMER_PERMISSION_COVERED_IN_ROLE,
  CUSTOMER_ROLE_WEIGHT,
  CUSTOMER_PERMISSION_WEIGHT,
  CUSTOMER_PERMISSIONS_NON_ASSIGNABLE,
} from '@/constants/customer.constants';
import { getNameValuesFromEnumNames } from './enum.helpers';

export const CUSTOMER_ROLES_SELECT_LIST: { name: string; value: CustomerRoleOnly }[] =
  getNameValuesFromEnumNames(CUSTOMER_ROLE_NAMES);

export const CUSTOMER_PERMISSIONS_SELECT_LIST: { name: string; value: CustomerPermissionOnly }[] =
  getNameValuesFromEnumNames(CUSTOMER_PERMISSION_NAMES);

export const getCustomerRoleNames = (
  selectedCustomerRoles?: CustomerRoleOnly[],
  rolesList: { name: string; value: CustomerRoleOnly }[] = CUSTOMER_ROLES_SELECT_LIST,
): { name: string; value: CustomerRoleOnly }[] => {
  const filtered = selectedCustomerRoles?.length
    ? rolesList.filter((role) => selectedCustomerRoles.includes(role.value))
    : rolesList;
  return [...filtered].sort((a, b) => CUSTOMER_ROLE_WEIGHT[a.value] - CUSTOMER_ROLE_WEIGHT[b.value]);
};

export const isPermissionNonAssignable = (permission: CustomerPermissionOnly): boolean =>
  CUSTOMER_PERMISSIONS_NON_ASSIGNABLE.includes(permission);

// Non-assignable permissions (e.g. Offer Coordinator) are shown only when the user already holds them
// (rendered disabled); otherwise they're hidden from the permission list entirely.
export const getVisiblePermissions = (
  permissionsList: { name: string; value: CustomerPermissionOnly }[],
  assignedPermissions: CustomerPermissionOnly[] = [],
): { name: string; value: CustomerPermissionOnly }[] =>
  permissionsList.filter(
    (permission) => !isPermissionNonAssignable(permission.value) || assignedPermissions.includes(permission.value),
  );

export const getCustomerPermissionNames = (
  selectedCustomerPermissions: CustomerPermissionOnly[] = [],
  permissionsList: { name: string; value: CustomerPermissionOnly }[] = CUSTOMER_PERMISSIONS_SELECT_LIST,
): { name: string; value: CustomerPermissionOnly }[] => {
  return permissionsList
    .filter((permission) => selectedCustomerPermissions.includes(permission.value))
    .sort((a, b) => CUSTOMER_PERMISSION_WEIGHT[a.value] - CUSTOMER_PERMISSION_WEIGHT[b.value]);
};

export const getCoveringPermissions = (roles: CustomerRoleOnly[]): CustomerPermissionOnly[] => {
  return Object.entries(CUSTOMER_PERMISSION_COVERED_IN_ROLE)
    .filter(([, coveringRole]) => roles.includes(coveringRole))
    .map(([permission]) => permission as CustomerPermissionOnly);
};

export const isPermissionEditable = (
  permission: CustomerPermissionOnly,
  editablePermissions: CustomerPermissionOnly[],
): boolean => editablePermissions.includes(permission);
